import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Button, Table, Modal, Form, Input, Select, DatePicker, message, Space, Card, Typography, Row, Col, Statistic, Tag, Collapse, Upload } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileExcelOutlined, FileAddOutlined, MailOutlined, SearchOutlined, UploadOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import type { TableProps, TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'antd/es/table/interface';
import type { FilterValue } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { handleApiError } from '../utils/errorHandler';
import InsurerImport from './InsurerImport';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Dragger } = Upload;

interface Insurer {
  id: string;
  name: string;
  email: string;
  phone: string;
  createdAt: string;
  updatedAt: string;
}

interface Metrics {
  totalInsurers: number;
  activeInsurers: number;
  inactiveInsurers: number;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const InsurersManagement: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const [insurers, setInsurers] = useState<Insurer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [editingInsurer, setEditingInsurer] = useState<Insurer | null>(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Total ${total} items`,
  });
  const [metrics, setMetrics] = useState<Metrics>({
    totalInsurers: 0,
    activeInsurers: 0,
    inactiveInsurers: 0
  });
  const [sortField, setSortField] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('ascend');
  const debouncedSearchText = useDebounce(searchText, 400);

  const fetchInsurers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: Insurer[] }>('/insurers', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          search: debouncedSearchText,
          startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
          sortField,
          sortOrder
        }
      });
      if (response.data.success) {
        setInsurers(response.data.data);
        calculateMetrics(response.data.data);
      } else {
        message.error('Failed to fetch insurers');
      }
    } catch (error) {
      console.error('Error fetching insurers:', error);
      message.error('Failed to fetch insurers');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, debouncedSearchText, dateRange, sortField, sortOrder]);

  const calculateMetrics = (data: Insurer[]) => {
    setMetrics({
      totalInsurers: data.length,
      activeInsurers: data.length, // You might want to add an active status field
      inactiveInsurers: 0
    });
  };

  useEffect(() => {
    fetchInsurers();
  }, [fetchInsurers]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [debouncedSearchText, dateRange]);

  const handleEdit = (insurer: Insurer) => {
    setEditingInsurer(insurer);
    setIsModalVisible(true);
    form.setFieldsValue(insurer);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/insurers/${id}`);
      message.success('Insurer deleted successfully');
      fetchInsurers();
    } catch (error) {
      console.error('Error deleting insurer:', error);
      message.error('Failed to delete insurer');
    }
  };

  const handleCreate = () => {
    setEditingInsurer(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingInsurer) {
        await api.put(`/insurers/${editingInsurer.id}`, values);
        message.success('Insurer updated successfully');
      } else {
        await api.post('/insurers', values);
        message.success('Insurer created successfully');
      }
      setIsModalVisible(false);
      fetchInsurers();
    } catch (error) {
      console.error('Error saving insurer:', error);
      message.error('Failed to save insurer');
    }
  };

  const handleImportSuccess = () => {
    setIsImportModalVisible(false);
    fetchInsurers();
    message.success('Import completed successfully!');
  };

  const columns: ColumnsType<Insurer> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (name: string, record: Insurer) => (
        <Button
          type="link"
          onClick={() => navigate(`/insurers/${record.id}`)}
          style={{ padding: 0, height: 'auto', fontSize: 'inherit' }}
        >
          {name}
        </Button>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/insurers/${record.id}`)}
            size="small"
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            size="small"
            danger
          />
        </Space>
      ),
    },
  ];

  const handleTableChange: TableProps<Insurer>['onChange'] = (pagination, filters, sorter) => {
    if (pagination) {
      setPagination(pagination);
    }

    if (sorter) {
      const { field, order } = sorter as { field: keyof Insurer; order: 'ascend' | 'descend' | null };
      const sortedData = [...insurers].sort((a, b) => {
        const aValue = a[field];
        const bValue = b[field];
        
        if (order === 'ascend') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        }
        if (order === 'descend') {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
        return 0;
      });
      setInsurers(sortedData);
    }
  };

  return (
    <div className="p-6">
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Insurers"
              value={metrics.totalInsurers}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Active Insurers"
              value={metrics.activeInsurers}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Inactive Insurers"
              value={metrics.inactiveInsurers}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="mt-6">
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>Insurers</Title>
          <Space>
            <Input
              placeholder="Search insurers..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <RangePicker
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                } else {
                  setDateRange(null);
                }
              }}
              allowClear
            />
            <Button
              icon={<UploadOutlined />}
              onClick={() => setIsImportModalVisible(true)}
              type="dashed"
            >
              Import CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Add Insurer
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={insurers}
          rowKey="id"
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          rowSelection={{
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => {
              setSelectedRowKeys(newSelectedRowKeys);
            },
          }}
        />
      </Card>

      {/* Import Modal */}
      <Modal
        title="Import Insurers from CSV"
        open={isImportModalVisible}
        onCancel={() => setIsImportModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <InsurerImport />
      </Modal>

      <Modal
        title={editingInsurer ? 'Edit Insurer' : 'New Insurer'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter insurer name' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: 'Please enter phone number' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InsurersManagement; 